import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Loader, AlertCircle, RefreshCw, Clock } from 'lucide-react';
import api from '../../utils/api';
import ColumnFilterDropdown from './ColumnFilterDropdown';

// T-1 §6.2: panel admin para ver/editar el estado por-toma de un cliente.
// Estados: sin_catalogado (0) → en_catalogado (1) → con_catalogado (2).
// Fuente de verdad: clients/{cid}.tomaStates. Endpoints en clientRoutes.js.

interface Props {
  clientId: string;
}

type TomaState = 'sin_catalogado' | 'en_catalogado' | 'con_catalogado';

interface TomaRow {
  toma: number;
  state: TomaState;
  updatedAt?: string;
  effective: boolean; // true si el estado viene reconciliado (no explícito en tomaStates)
}

const STATE_ORDER: TomaState[] = ['sin_catalogado', 'en_catalogado', 'con_catalogado'];

const STATE_BADGE: Record<TomaState, string> = {
  sin_catalogado: 'bg-gray-100 text-gray-700',
  en_catalogado: 'bg-amber-100 text-amber-700',
  con_catalogado: 'bg-green-100 text-green-700',
};

const STATE_LABEL: Record<TomaState, string> = {
  sin_catalogado: 'Sin catalogado',
  en_catalogado: 'En catalogado',
  con_catalogado: 'Con catalogado',
};

const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

const formatTs = (iso?: string) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
};

const TomaStatesPanel: React.FC<Props> = ({ clientId }) => {
  const [rows, setRows] = useState<TomaRow[]>([]);
  const [policy, setPolicy] = useState<string>('');
  const [lastProcessedToma, setLastProcessedToma] = useState<number>(0);
  const [tomaStatesUpdatedAt, setTomaStatesUpdatedAt] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savingToma, setSavingToma] = useState<number | null>(null);
  // PT-#32 — filtro tipo Excel de la columna Estado (vacío = todas las filas).
  const [estadoFilter, setEstadoFilter] = useState<TomaState[]>([]);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/api/clients/${clientId}/toma-states`);
      setRows(Array.isArray(data.tomas) ? data.tomas : []);
      setPolicy(data.policy || '');
      setLastProcessedToma(Number(data.lastProcessedToma) || 0);
      setTomaStatesUpdatedAt(data.tomaStatesUpdatedAt);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Error cargando estados de toma');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = async (toma: number, newState: TomaState) => {
    setSavingToma(toma);
    setError(null);
    try {
      await api.put(`/api/clients/${clientId}/toma-states`, { toma, state: newState });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Error guardando estado');
    } finally {
      setSavingToma(null);
    }
  };

  // Opciones distintas presentes en las filas (orden canónico STATE_ORDER) + count.
  // Se derivan de los datos → si un estado no aparece, no ensucia el dropdown.
  const estadoOptions = useMemo(() => {
    const counts = new Map<TomaState, number>();
    for (const r of rows) counts.set(r.state, (counts.get(r.state) || 0) + 1);
    return STATE_ORDER
      .filter(s => counts.has(s))
      .map(s => ({ value: s, label: STATE_LABEL[s], count: counts.get(s) || 0 }));
  }, [rows]);

  // Filtrado in-place (client-side). Preserva el orden que vino del server.
  const visibleRows = useMemo(
    () => (estadoFilter.length === 0 ? rows : rows.filter(r => estadoFilter.includes(r.state))),
    [rows, estadoFilter]
  );

  const isStale = (row: TomaRow): boolean => {
    if (row.state !== 'en_catalogado' || !row.updatedAt) return false;
    const ts = new Date(row.updatedAt).getTime();
    return Number.isFinite(ts) && Date.now() - ts > THREE_HOURS_MS;
  };

  return (
    <div className="mt-4 border-t pt-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-800">Estados de tomas</h4>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refrescar
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-3">
        Política actual: <span className="font-medium">{policy || '—'}</span>
        {' · '}Última toma procesada: <span className="font-medium tabular-nums">{lastProcessedToma}</span>
        {tomaStatesUpdatedAt && (
          <>{' · '}Actualizado: <span className="tabular-nums">{formatTs(tomaStatesUpdatedAt)}</span></>
        )}
      </p>

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mb-2">
          <AlertCircle className="w-3.5 h-3.5" />
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
          <Loader className="w-4 h-4 animate-spin" />
          Cargando…
        </div>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400 py-2">No hay tomas registradas para este cliente.</p>
      ) : (
        <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left text-gray-500">
                <th className="px-3 py-2 font-medium">Toma</th>
                <th className="px-3 py-2 font-medium">
                  <ColumnFilterDropdown
                    label="Estado"
                    options={estadoOptions}
                    selected={estadoFilter}
                    onChange={(vals) => setEstadoFilter(vals as TomaState[])}
                  />
                </th>
                <th className="px-3 py-2 font-medium">Actualizado</th>
                <th className="px-3 py-2 font-medium">Cambiar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-gray-400">
                    Ninguna toma coincide con el filtro de Estado.
                  </td>
                </tr>
              ) : visibleRows.map((row) => (
                <tr key={row.toma} className={isStale(row) ? 'bg-amber-50' : ''}>
                  <td className="px-3 py-2 tabular-nums font-medium text-gray-700">
                    #{row.toma}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full ${STATE_BADGE[row.state]}`}>
                      {STATE_LABEL[row.state]}
                    </span>
                    {!row.effective && (
                      <span className="ml-1 text-[10px] text-gray-400">(reconciliado)</span>
                    )}
                    {isStale(row) && (
                      <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] text-amber-700">
                        <Clock className="w-3 h-3" />
                        &gt;3h
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-gray-500">
                    {formatTs(row.updatedAt)}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={row.state}
                      disabled={savingToma === row.toma}
                      onChange={(e) => handleChange(row.toma, e.target.value as TomaState)}
                      className="text-xs border border-gray-300 rounded px-2 py-1 disabled:opacity-50"
                    >
                      {STATE_ORDER.map((s) => (
                        <option key={s} value={s}>
                          {STATE_LABEL[s]}
                        </option>
                      ))}
                    </select>
                    {savingToma === row.toma && (
                      <Loader className="w-3 h-3 animate-spin inline-block ml-1 text-gray-400" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TomaStatesPanel;
